/**
 * "Edit shipment details" for an ASN — the courier and every other field the
 * API lets the sender change after creation.
 *
 * Offered only when the API's `can_edit_details` flag is set (an admin at the
 * sending site), so the control never appears for a user the backend would
 * refuse. Only the fields that actually changed are sent: an untouched
 * tracking number is never re-registered with the courier, and a courier
 * change re-points tracking at the new carrier server-side.
 */
import { useState } from 'react';
import { purchasingAPI, UpdateASNRequest } from '../../lib/api';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { COURIER_OPTIONS, TRANSPORT_METHOD_OPTIONS } from './asnCourier';

/** The ASN fields this editor reads; both the list and detail shapes satisfy it. */
export interface EditableASN {
  id: number;
  asn_number: string;
  courier?: string;
  courier_other?: string;
  tracking_number?: string;
  method_of_transport?: string;
  shipment_date?: string | null;
  estimated_delivery_date?: string | null;
  weight_kg?: string | number | null;
  notes?: string;
  /** Whether live courier tracking is on, so the dialog only mentions it then. */
  tracking_pro_enabled?: boolean;
  /** Server-resolved: an admin at the sending site on a non-cancelled ASN. */
  can_edit_details?: boolean;
}

interface ASNEditDetailsProps {
  asn: EditableASN;
  /** Called after a successful save so the caller can refetch. */
  onSaved: () => void;
  /** `icon` for a table row, `button` for the detail header. */
  variant?: 'icon' | 'button';
}

type FormState = Record<
  'courier' | 'courier_other' | 'tracking_number' | 'method_of_transport'
  | 'shipment_date' | 'estimated_delivery_date' | 'weight_kg' | 'notes',
  string
>;

/** The ASN's current values as form strings (dates as yyyy-mm-dd). */
function toForm(asn: EditableASN): FormState {
  return {
    courier: asn.courier || '',
    courier_other: asn.courier_other || '',
    tracking_number: asn.tracking_number || '',
    method_of_transport: asn.method_of_transport || '',
    shipment_date: (asn.shipment_date || '').slice(0, 10),
    estimated_delivery_date: (asn.estimated_delivery_date || '').slice(0, 10),
    weight_kg: asn.weight_kg === null || asn.weight_kg === undefined ? '' : String(asn.weight_kg),
    notes: asn.notes || '',
  };
}

/** A validation message for the form, or null when it can be saved. */
function validate(form: FormState, initial: FormState): string | null {
  if (!form.courier) return 'Choose a courier.';
  if (form.courier === 'other' && !form.courier_other.trim()) return 'Enter the courier name.';
  const weight = form.weight_kg.trim();
  if (!weight && initial.weight_kg.trim()) return 'Enter the weight.';
  // Same rule as the model: at least 0.01 kg, at most 2 decimal places.
  if (weight && !(/^\d+(\.\d{1,2})?$/.test(weight) && Number(weight) >= 0.01)) {
    return 'Weight must be at least 0.01 kg, with up to 2 decimal places.';
  }
  if (form.shipment_date && form.estimated_delivery_date && form.estimated_delivery_date < form.shipment_date) {
    return 'Expected delivery cannot be before the shipment date.';
  }
  return null;
}

/** Only the changed fields, in the shape the API accepts (blank dates/weight as null). */
function changedFields(initial: FormState, form: FormState): UpdateASNRequest {
  const next: FormState = { ...form, courier_other: form.courier === 'other' ? form.courier_other.trim() : '' };
  const patch: UpdateASNRequest = {};
  (Object.keys(next) as (keyof FormState)[]).forEach((key) => {
    const value = key === 'notes' ? next[key] : next[key].trim();
    if (value === initial[key].trim()) return;
    if (key === 'shipment_date' || key === 'estimated_delivery_date' || key === 'weight_kg') patch[key] = value || null;
    else patch[key] = value;
  });
  return patch;
}

/** The API's first field error (e.g. "weight kg: …"), else its message. */
function saveError(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { details?: { fields?: Record<string, string[] | string> } } | undefined;
    const [name, problems] = Object.entries(data?.details?.fields || {})[0] || [];
    if (name) return `${name.replace(/_/g, ' ')}: ${Array.isArray(problems) ? problems[0] : problems}`;
  }
  return getApiErrorMessage(err, 'Failed to update the ASN');
}

/**
 * Edit button plus the dialog it opens. Renders nothing unless the API says
 * the current user may edit this ASN's details.
 *
 * @param asn - The ASN to edit; `can_edit_details` decides whether the control shows.
 * @param onSaved - Fired after a successful save so the caller can refetch.
 * @param variant - `icon` for a table row, `button` for a header.
 * @returns The edit control, or null when editing is not permitted.
 */
export default function ASNEditDetails({ asn, onSaved, variant = 'button' }: ASNEditDetailsProps) {
  const [open, setOpen] = useState(false);
  const [initial, setInitial] = useState<FormState>(() => toForm(asn));
  const [form, setForm] = useState<FormState>(() => toForm(asn));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (!asn.can_edit_details) return null;

  const handleOpenChange = (next: boolean) => {
    if (saving) return;
    if (next) {
      // Re-read the row each time: the list may have refetched since mount.
      const current = toForm(asn);
      setInitial(current);
      setForm(current);
      setError(null);
    }
    setOpen(next);
  };

  const set = (key: keyof FormState, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    const problem = validate(form, initial);
    if (problem) {
      setError(problem);
      return;
    }
    const patch = changedFields(initial, form);
    if (Object.keys(patch).length === 0) {
      setOpen(false);
      return;
    }
    try {
      setSaving(true);
      await purchasingAPI.updateASN(asn.id, patch);
      toast.success(`ASN ${asn.asn_number} updated`);
      setOpen(false);
      onSaved();
    } catch (err: unknown) {
      setError(saveError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* A real trigger, so Radix returns focus to it when the dialog closes. */}
      <DialogTrigger asChild>
        {variant === 'icon' ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            title="Edit shipment details"
            aria-label={`Edit shipment details for ASN ${asn.asn_number}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        ) : (
          <Button type="button" variant="outline" size="sm">
            <Pencil className="h-3.5 w-3.5 mr-1" />
            Edit details
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit ASN {asn.asn_number}</DialogTitle>
          <DialogDescription>
            {asn.tracking_pro_enabled
              ? 'Changing the courier or tracking number re-registers live courier tracking.'
              : 'Correct the courier and shipment details for this ASN.'}
          </DialogDescription>
        </DialogHeader>
        {/* noValidate: the app's own checks (and messages) run, not the browser's. */}
        <form noValidate onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="asn-edit-courier">Courier</Label>
              <Select value={form.courier} onValueChange={(v) => set('courier', v)} disabled={saving}>
                <SelectTrigger id="asn-edit-courier"><SelectValue placeholder="Choose a courier" /></SelectTrigger>
                <SelectContent>
                  {COURIER_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="asn-edit-tracking">Tracking Number</Label>
              <Input id="asn-edit-tracking" className="font-mono" value={form.tracking_number} disabled={saving} maxLength={100}
                onChange={(e) => set('tracking_number', e.target.value)} placeholder="Consignment number" />
            </div>
            {form.courier === 'other' && (
              <div className="space-y-1.5 col-span-2">
                <Label htmlFor="asn-edit-courier-other">Courier Name</Label>
                <Input id="asn-edit-courier-other" value={form.courier_other} disabled={saving} maxLength={100}
                  onChange={(e) => set('courier_other', e.target.value)} placeholder="Enter courier name" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="asn-edit-transport">Transport Method</Label>
              <Select value={form.method_of_transport} onValueChange={(v) => set('method_of_transport', v)} disabled={saving}>
                <SelectTrigger id="asn-edit-transport"><SelectValue placeholder="Choose a method" /></SelectTrigger>
                <SelectContent>
                  {TRANSPORT_METHOD_OPTIONS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="asn-edit-weight">Weight (kg)</Label>
              <Input id="asn-edit-weight" type="number" step="0.01" min="0.01" value={form.weight_kg} disabled={saving}
                onChange={(e) => set('weight_kg', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="asn-edit-shipment-date">Shipment Date</Label>
              <Input id="asn-edit-shipment-date" type="date" value={form.shipment_date} disabled={saving}
                onChange={(e) => set('shipment_date', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="asn-edit-expected">Expected Delivery</Label>
              <Input id="asn-edit-expected" type="date" value={form.estimated_delivery_date} disabled={saving}
                onChange={(e) => set('estimated_delivery_date', e.target.value)} />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label htmlFor="asn-edit-notes">Notes</Label>
              <Textarea id="asn-edit-notes" rows={3} value={form.notes} disabled={saving}
                onChange={(e) => set('notes', e.target.value)} />
            </div>
          </div>
          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
